import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, Save, Search, ChevronLeft, UserPlus, AlertTriangle } from 'lucide-react';
import { crmApi } from '@/api/crm';
import { operationsApi } from '@/api/operations';
import { useCreerProforma, useModifierProforma, useMesProformas } from '@/hooks/useCommercial';
import { NouveauClientDialog } from './NouveauClientDialog';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Créer une proforma.
 *
 * Le commercial choisit un client (ou en crée un rapidement s'il s'agit d'un
 * prospect), ajoute des lignes depuis le catalogue béton, applique une remise
 * globale éventuelle, puis enregistre en brouillon (statut `devis`). L'étape
 * signature + soumission DAF se fait ensuite sur la page ProformasPage.
 */

interface LigneBrouillon {
  produitId: string;
  quantite:  number;
  remise:    number;
}

export const NouvelleProformaPage = () => {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get('edit');

  const [clientId, setClientId]   = useState<string>('');
  const [clientQuery, setCQ]      = useState('');
  const [dateLivr, setDateLivr]   = useState('');
  const [remisePct, setRemisePct] = useState(0);
  const [notes, setNotes]         = useState('');
  const [lignes, setLignes]       = useState<LigneBrouillon[]>([]);
  const [showProduits, setShowP]  = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);

  const creer     = useCreerProforma();
  const modifier  = useModifierProforma();
  const { data: mesProformas = [] } = useMesProformas();
  const proformaEdit = editId ? mesProformas.find((p) => p.id === editId) : undefined;

  const contactsQ = useQuery({
    queryKey: ['crm', 'contacts', 'commercial', clientQuery],
    queryFn:  () => crmApi.getContacts({
      search: clientQuery || undefined,
      limit:  20,
    }),
    staleTime: 30_000,
  });

  const produitsQ = useQuery({
    queryKey: ['operations', 'produits', 'commercial'],
    queryFn:  () => operationsApi.beton.listProduits({ actif: true, limit: 200 }),
    staleTime: 5 * 60_000,
  });

  const produitsById = useMemo(() => {
    const map = new Map<string, any>();
    (produitsQ.data?.data ?? []).forEach((p: any) => map.set(p.id, p));
    return map;
  }, [produitsQ.data]);

  /**
   * Quantité engagée par produit sur CETTE proforma. Deux lignes du même
   * produit puisent dans le même parc : comparer chaque ligne isolément au
   * disponible laisserait passer une double promesse sur le même stock.
   */
  const demandeParProduit = useMemo(() => {
    const m = new Map<string, number>();
    lignes.forEach((l) => m.set(l.produitId, (m.get(l.produitId) ?? 0) + (l.quantite || 0)));
    return m;
  }, [lignes]);

  // Mode édition — pré-remplissage une seule fois quand la proforma cible est chargée.
  useEffect(() => {
    if (!editId || prefilled || !proformaEdit) return;
    setClientId(proformaEdit.clientId);
    setDateLivr(proformaEdit.dateLivraisonSouhaitee ? proformaEdit.dateLivraisonSouhaitee.slice(0, 10) : '');
    setRemisePct(Number(proformaEdit.remisePct ?? 0));
    setNotes(proformaEdit.notes ?? '');
    setLignes(
      (proformaEdit.lignes ?? []).map((l) => ({
        produitId: l.produitId,
        quantite:  Number(l.quantite),
        remise:    Number(l.remiseLignePct ?? 0),
      })),
    );
    setPrefilled(true);
  }, [editId, prefilled, proformaEdit]);

  const totaux = useMemo(() => {
    let ht = 0;
    for (const l of lignes) {
      const p = produitsById.get(l.produitId);
      if (!p || !l.quantite) continue;
      const pu = Number(p.prixVenteCents) / 100;
      ht += pu * l.quantite * (1 - (l.remise ?? 0) / 100);
    }
    const htNet = Math.round(ht * (1 - (remisePct ?? 0) / 100));
    return { htBrut: Math.round(ht), htNet, ttc: Math.round(htNet * 1.18) };
  }, [lignes, remisePct, produitsById]);

  const clientChoisi = (contactsQ.data?.data ?? []).find((c: any) => c.id === clientId);
  const canSubmit = clientId !== '' && lignes.length > 0 && lignes.every((l) => l.quantite > 0);

  const missings: string[] = [];
  if (!clientId) missings.push('Client');
  if (lignes.length === 0) missings.push('Au moins une ligne');
  if (lignes.some((l) => !l.quantite || l.quantite <= 0)) missings.push('Quantités > 0');

  const enregistrer = async () => {
    if (!canSubmit) return;
    const payload = {
      clientId,
      dateLivraisonSouhaitee: dateLivr || undefined,
      remisePct: remisePct || undefined,
      notes:     notes.trim() || undefined,
      lignes: lignes.map((l) => ({
        produitId:      l.produitId,
        quantite:       Number(l.quantite),
        remiseLignePct: l.remise > 0 ? Number(l.remise) : undefined,
      })),
    };
    const proforma = editId
      ? await modifier.mutateAsync({ id: editId, body: payload })
      : await creer.mutateAsync(payload);
    nav(`/commercial/proformas?highlight=${proforma.id}`);
  };

  return (
    <div>
      {/* Header page */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => nav(-1)}
          aria-label="Retour"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--tx-2, #374151)' }}
        >
          <ChevronLeft size={20} />
        </button>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>
          {editId ? `Modifier ${proformaEdit?.reference ?? 'la proforma'}` : 'Nouvelle proforma'}
        </h1>
      </div>

      {/* Bloc 1 : Client */}
      <Section titre="Client">
        {clientChoisi ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 700 }}>{clientChoisi.fullName}</div>
              {clientChoisi.companyName && (
                <div style={{ fontSize: 13, color: 'var(--tx-3, #6B7280)' }}>{clientChoisi.companyName}</div>
              )}
              <div style={{ fontSize: 12, color: 'var(--tx-3, #6B7280)' }}>{clientChoisi.phone ?? ''}</div>
            </div>
            <button
              onClick={() => setClientId('')}
              style={{
                background: 'transparent', border: '1px solid var(--bd-1, #E5E7EB)',
                borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12,
              }}
            >
              Changer
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--tx-3, #6B7280)' }} />
                <input
                  autoFocus
                  type="text"
                  value={clientQuery}
                  onChange={(e) => setCQ(e.target.value)}
                  placeholder="Rechercher un client / prospect…"
                  style={{
                    width: '100%', padding: '8px 12px 8px 32px',
                    border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 14,
                  }}
                />
              </div>
              <button
                onClick={() => setShowNewClient(true)}
                title="Créer un nouveau client / prospect"
                style={{
                  padding: '8px 12px', borderRadius: 8, border: '1px dashed var(--pr-1, #1F4E79)',
                  background: '#EFF6FF', color: 'var(--pr-1, #1F4E79)', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                <UserPlus size={14} /> Nouveau
              </button>
            </div>
            <div style={{
              maxHeight: 260, overflow: 'auto', border: '1px solid var(--bd-1, #E5E7EB)',
              borderRadius: 8, background: 'var(--bg-1, #FAFAFA)',
            }}>
              {(contactsQ.data?.data ?? []).map((c: any) => (
                <button
                  key={c.id}
                  onClick={() => setClientId(c.id)}
                  style={{
                    width: '100%', textAlign: 'left', padding: '10px 12px',
                    background: 'transparent', border: 'none', borderBottom: '1px solid var(--bd-1, #E5E7EB)',
                    cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {c.fullName} {c.companyName ? <span style={{ color: 'var(--tx-3, #6B7280)', fontWeight: 400 }}>· {c.companyName}</span> : null}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)' }}>
                    {c.contactType === 'prospect' ? 'Prospect' : 'Client'} · {c.phone ?? '—'}
                  </span>
                </button>
              ))}
              {(contactsQ.data?.data ?? []).length === 0 && !contactsQ.isLoading && (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--tx-3, #6B7280)', fontSize: 13 }}>
                  Aucun contact. Clique <strong>Nouveau</strong> pour en créer un.
                </div>
              )}
            </div>
          </>
        )}
      </Section>

      {/* Bloc 2 : Lignes */}
      <Section titre="Lignes de la proforma">
        {lignes.map((l, i) => {
          const p = produitsById.get(l.produitId);
          const pu = p ? Number(p.prixVenteCents) / 100 : 0;
          const total = Math.round(pu * l.quantite * (1 - (l.remise ?? 0) / 100));
          // Disponible au parc : entré et pas sorti. Le manque ne bloque pas la
          // proforma — on vend aussi de la production à venir — mais le
          // commercial doit le savoir AVANT de promettre une date au client.
          const dispo   = Number(p?.disponibleParc ?? 0);
          const demande = demandeParProduit.get(l.produitId) ?? 0;
          const manque  = demande - dispo;
          return (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center',
              padding: 10, background: 'var(--bg-1, #FAFAFA)', borderRadius: 8, marginBottom: 8,
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{p?.code ?? '?'} · {p?.designation ?? ''}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                  <label style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)' }}>Qté</label>
                  <input
                    type="number" min={1} value={l.quantite}
                    onChange={(e) => setLignes(lignes.map((x, j) => j === i ? { ...x, quantite: Number(e.target.value) } : x))}
                    style={{ width: 80, padding: '4px 8px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 6, fontSize: 13 }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)' }}>{p?.uniteVente ?? ''}</span>
                  <label style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)', marginLeft: 8 }}>Rem. %</label>
                  <input
                    type="number" min={0} max={30} value={l.remise}
                    onChange={(e) => setLignes(lignes.map((x, j) => j === i ? { ...x, remise: Number(e.target.value) } : x))}
                    style={{ width: 60, padding: '4px 8px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 6, fontSize: 13 }}
                  />
                </div>
                <div style={{ marginTop: 6, fontSize: 13, fontFamily: 'monospace', fontWeight: 700 }}>
                  {total.toLocaleString('fr-FR')} FCFA
                </div>
                {manque > 0 ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
                    fontSize: 11, fontWeight: 600, color: '#B45309',
                  }}>
                    <AlertTriangle size={13} />
                    <span>
                      {dispo.toLocaleString('fr-FR')} {p?.uniteVente ?? ''} au parc — il en manque{' '}
                      {manque.toLocaleString('fr-FR')} à produire
                    </span>
                  </div>
                ) : (
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--tx-3, #6B7280)' }}>
                    {dispo.toLocaleString('fr-FR')} {p?.uniteVente ?? ''} disponibles au parc
                  </div>
                )}
              </div>
              <button
                onClick={() => setLignes(lignes.filter((_, j) => j !== i))}
                aria-label="Supprimer la ligne"
                style={{ background: 'transparent', border: 'none', color: '#B91C1C', cursor: 'pointer', padding: 8 }}
              >
                <Trash2 size={18} />
              </button>
            </div>
          );
        })}

        {!showProduits ? (
          <button
            onClick={() => setShowP(true)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', padding: '12px', background: 'var(--bg-1, #FAFAFA)',
              border: '2px dashed var(--bd-2, #D1D5DB)', borderRadius: 8,
              cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'var(--tx-2, #374151)',
            }}
          >
            <Plus size={16} /> Ajouter une ligne
          </button>
        ) : (
          <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 8, background: 'var(--bg-1, #FAFAFA)' }}>
            {(produitsQ.data?.data ?? []).map((p: any) => (
              <button
                key={p.id}
                onClick={() => {
                  setLignes([...lignes, { produitId: p.id, quantite: 1, remise: 0 }]);
                  setShowP(false);
                }}
                style={{
                  width: '100%', textAlign: 'left', padding: '10px 12px',
                  background: 'transparent', border: 'none', borderBottom: '1px solid var(--bd-1, #E5E7EB)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{p.code} · {p.designation}</div>
                <div style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)' }}>
                  {(Number(p.prixVenteCents) / 100).toLocaleString('fr-FR')} FCFA / {p.uniteVente}
                  {' · '}
                  <span style={{
                    fontWeight: 600,
                    color: Number(p.disponibleParc ?? 0) > 0 ? 'var(--success, #047857)' : '#B45309',
                  }}>
                    {Number(p.disponibleParc ?? 0).toLocaleString('fr-FR')} au parc
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </Section>

      {/* Bloc 3 : Options */}
      <Section titre="Options">
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Date livraison souhaitée">
            <input
              type="date" value={dateLivr} onChange={(e) => setDateLivr(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 14, width: '100%' }}
            />
          </Field>
          <Field label="Remise globale (%)">
            <input
              type="number" min={0} max={15} value={remisePct}
              onChange={(e) => setRemisePct(Number(e.target.value))}
              style={{ padding: '8px 12px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 14, width: '100%' }}
            />
          </Field>
          <Field label="Notes">
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Conditions particulières, ref bon commande client, etc."
              style={{ padding: '8px 12px', border: '1px solid var(--bd-2, #D1D5DB)', borderRadius: 8, fontSize: 14, width: '100%', resize: 'vertical' }}
            />
          </Field>
        </div>
      </Section>

      {/* Totaux */}
      <div style={{
        background: '#fff', border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 12,
        padding: 16, marginBottom: 16,
      }}>
        <Row label="Total HT brut" value={totaux.htBrut.toLocaleString('fr-FR') + ' FCFA'} />
        {remisePct > 0 && <Row label={`Remise globale (${remisePct}%)`} value={'-' + Math.round(totaux.htBrut - totaux.htNet).toLocaleString('fr-FR') + ' FCFA'} tone="warning" />}
        <Row label="Total HT net" value={totaux.htNet.toLocaleString('fr-FR') + ' FCFA'} bold />
        <Row label="TVA 18%" value={(totaux.ttc - totaux.htNet).toLocaleString('fr-FR') + ' FCFA'} muted />
        <Row label="Total TTC" value={totaux.ttc.toLocaleString('fr-FR') + ' FCFA'} bold big />
      </div>

      {!canSubmit && (
        <div style={{ marginBottom: 12, fontSize: 13, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px' }}>
          À compléter : <strong>{missings.join(', ')}</strong>
        </div>
      )}

      <button
        onClick={enregistrer}
        disabled={!canSubmit || creer.isPending || modifier.isPending}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          width: '100%', padding: '14px',
          background: canSubmit ? 'var(--pr-1, #1F4E79)' : '#D1D5DB',
          color: '#fff', border: 'none', borderRadius: 12,
          fontWeight: 700, fontSize: 15,
          cursor: canSubmit ? 'pointer' : 'not-allowed',
        }}
      >
        <Save size={18} />
        {creer.isPending || modifier.isPending
          ? 'Enregistrement…'
          : editId ? 'Enregistrer les modifications' : 'Enregistrer la proforma'}
      </button>

      {showNewClient && (
        <NouveauClientDialog
          onClose={() => setShowNewClient(false)}
          onCreated={(id) => { setClientId(id); setShowNewClient(false); }}
        />
      )}
    </div>
  );
};

function Section({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tx-3, #6B7280)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>{titre}</div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-2, #374151)', marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}

function Row({ label, value, bold, muted, big, tone }: { label: string; value: string; bold?: boolean; muted?: boolean; big?: boolean; tone?: 'warning' }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '4px 0', fontSize: big ? 16 : 14,
      fontWeight: bold ? 700 : 400,
      color: muted ? 'var(--tx-3, #6B7280)' : tone === 'warning' ? '#B45309' : 'var(--tx-1, #111827)',
    }}>
      <span>{label}</span>
      <span style={{ fontFamily: 'monospace' }}>{value}</span>
    </div>
  );
}

